// src/components/PatientInfoForm.js
import React, { useCallback, useMemo } from "react";
import {
  TextInput,
  PasswordInput,
  Dropdown,
  NumberInput,
  DatePicker,
  DatePickerInput,
} from "@carbon/react";

import PartialDateInput from "./PartialDateInput";
import SegmentedTiles from "./SegmentedTiles";

import {
  SECTION_GROUPS,
  labelOf,
  typeOf,
  isRequired,
} from "../schema/patientSchema";

/* ======================
 * 共通ラッパ
 * ====================== */
const FieldWrap = React.memo(({ children, full }) => (
  <div
    style={{
      flex: full ? "1 1 100%" : "1 1 45%",
      minWidth: 280,
    }}
  >
    {children}
  </div>
));

const RequiredBadge = React.memo(() => (
  <span style={{ color: "#da1e28", marginLeft: 4 }}>*必須</span>
));

const FieldLabel = React.memo(({ k, label, required = false }) => (
  <div style={{ marginBottom: 4, fontWeight: 600 }}>
    {label || labelOf(k)}
    {(required || isRequired(k)) && <RequiredBadge />}
  </div>
));

/* ======================
 * 単一フィールド（安定化）
 * ====================== */
const Field = React.memo(function Field({ k, value, onChange, options }) {
  const t = typeOf(k);

  const selectedItem = useMemo(() => {
    if (t !== "enum") return null;
    return (options[k] || []).find((it) => it.id === value) ?? null;
  }, [t, k, value, options]);

  switch (t) {
    case "string":
      return (
        <TextInput
          id={k}
          labelText=""
          value={value ?? ""}
          onChange={(e) => onChange(k, e.target.value)}
        />
      );

    case "password":
      return (
        <PasswordInput
          id={k}
          labelText=""
          value={value ?? ""}
          onChange={(e) => onChange(k, e.target.value)}
        />
      );

    case "enum": {
      const items = options[k] || [];

      if (items.length <= 3) {
        return (
          <SegmentedTiles
            id={k}
            items={items}
            selectedItem={selectedItem}
            onChange={(item) => onChange(k, item?.id ?? "")}
          />
        );
      }

      return (
        <Dropdown
          id={k}
          items={items}
          selectedItem={selectedItem}
          itemToString={(it) => it?.text ?? ""}
          onChange={({ selectedItem }) =>
            onChange(k, selectedItem?.id ?? "")
          }
          label=""
        />
      );
    }

    case "date":
      return (
        <DatePicker
          datePickerType="single"
          dateFormat="Y/m/d"
          value={value ? [new Date(value)] : []}
          onChange={(dates) =>
            onChange(k, dates[0]?.toISOString() ?? "")
          }
        >
          <DatePickerInput
            id={`${k}-input`}
            labelText=""
            placeholder="年/月/日"
          />
        </DatePicker>
      );

    case "partialDate":
      return (
        <PartialDateInput
          id={k}
          label=""
          value={value ?? ""}
          onChange={(_, vv) => onChange(k, vv)}
          showHint
        />
      );

    case "decimal1":
      return (
        <NumberInput
          id={k}
          label=""
          min={0}
          max={10}
          step={0.5}
          allowEmpty
          value={value === "" ? "" : Number(value)}
          onChange={(_, data) =>
            onChange(k, data.value ?? "")
          }
        />
      );

    default:
      return null;
  }
});

/* ======================
 * セクション
 * ====================== */
const Section = React.memo(function Section({
  title,
  keys,
  values,
  onChange,
  options,
  includeEdss,
  extraRequiredKeys,
}) {
  const ks = includeEdss ? keys : keys.filter((k) => k !== "edss_score");

  return (
    <section style={{ marginBottom: 32 }}>
      <h4>{title}</h4>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
        {ks.map((k) => (
          <FieldWrap
            key={k}
            full={[
              "complications",
              "medical_history",
              "family_history",
            ].includes(k)}
          >
            <FieldLabel
              k={k}
              required={extraRequiredKeys.includes(k)}
            />
            <Field
              k={k}
              value={values[k]}
              onChange={onChange}
              options={options}
            />
          </FieldWrap>
        ))}
      </div>
    </section>
  );
});

/* ======================
 * メイン
 * ====================== */
export default function PatientInfoForm({
  values,
  onChange,
  options,
  includeEdss = true,
  includeDrugHistory = false,
  extraRequiredKeys = [],
}) {
  const v = values ?? {};
  const stableOnChange = useCallback(onChange, [onChange]);

  /* ====== 薬剤履歴（完全維持） ====== */
  const drugOptions = [
    "ケシンプタ（オファツムマブ）",
    "メーゼント（シポニモド）",
    "テクフィデラ（フマル酸ジメチル）",
    "タイサブリ（ナタリズマブ）",
    "イムセラ・ジレニア（フィンゴリモド）",
    "コパキソン（グラチラマー酢酸塩）",
    "アボネックス（IFNβ-1a）",
    "ベタフェロン（IFNβ-1b）",
    "無治療",
  ];

  const DrugHistory = React.memo(() => {
    if (!includeDrugHistory) return null;

    return (
      <section style={{ marginBottom: 32 }}>
        <h4>治療薬履歴</h4>

        {[...Array(10)].map((_, i) => {
          const idx = i + 1;
          const dk = `treatment_drug_${idx}`;
          const sk = `treatment_start_${idx}`;

          const isDrugRequired = extraRequiredKeys.includes(dk);
          const isStartRequired = extraRequiredKeys.includes(sk);

          return (
            <div
              key={idx}
              style={{ display: "flex", gap: 16, marginBottom: 12 }}
            >
              <div style={{ flex: "1 1 60%" }}>
                <FieldLabel
                  k={dk}
                  label={`治療薬${idx}`}
                  required={isDrugRequired}
                />
                <Dropdown
                  id={dk}
                  label=""
                  items={drugOptions}
                  selectedItem={v[dk] || null}
                  itemToString={(it) => it ?? ""}
                  onChange={({ selectedItem }) =>
                    stableOnChange(dk, selectedItem ?? "")
                  }
                />
              </div>

              <div style={{ flex: "1 1 40%" }}>
                <FieldLabel
                  k={sk}
                  label={`治療開始日${idx}`}
                  required={isStartRequired}
                />
                <PartialDateInput
                  id={sk}
                  label=""
                  value={v[sk] ?? ""}
                  onChange={(_, vv) =>
                    stableOnChange(sk, vv)
                  }
                  showHint
                />
              </div>
            </div>
          );
        })}
      </section>
    );
  });

  return (
    <>
      <Section
        title="個人情報"
        keys={SECTION_GROUPS.personal}
        values={v}
        onChange={stableOnChange}
        options={options}
        includeEdss={includeEdss}
        extraRequiredKeys={extraRequiredKeys}
      />
      <Section
        title="症状・病歴"
        keys={SECTION_GROUPS.history}
        values={v}
        onChange={stableOnChange}
        options={options}
        includeEdss={includeEdss}
        extraRequiredKeys={extraRequiredKeys}
      />
      <Section
        title="検査結果"
        keys={SECTION_GROUPS.lab}
        values={v}
        onChange={stableOnChange}
        options={options}
        includeEdss={includeEdss}
        extraRequiredKeys={extraRequiredKeys}
      />
      <DrugHistory />
    </>
  );
}